import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { LandingPageCard } from '../components/LandingPageCard';
import { LandingSettingsDialog } from '../components/LandingSettingsDialog';
import { OutreachProgressWidget } from '../components/OutreachProgressWidget';
import { SequenceTodayBar } from '../components/SequenceTodayBar';
import { LetterTemplateDialog } from '../components/LetterTemplateDialog';
import {
  Loader2, Trash2, Copy, Check, ArrowLeft, FileText, Download,
  PenTool, ChevronRight, CheckCircle, Send, FileJson, FileArchive, ThumbsUp, ThumbsDown,
  AlertTriangle, Reply, Eye, PhoneIncoming, QrCode, MailX, PhoneOutgoing, Undo2, Lock, History, UserRound, ChevronDown, Wand2, Heading,
} from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { LetterHistoryDialog } from '../components/LetterHistoryDialog';
import { LetterDupeBadge } from '../components/LetterDupeBadge';
import { PRIO_STYLES } from '../components/PrioCriteriaPicker';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  draft: { label: 'Draft', class: 'text-amber-400 bg-amber-400/10 border-amber-500/30' },
  finalized: { label: 'Finalized', class: 'text-purple-400 bg-purple-400/10 border-purple-500/30' },
  approved: { label: 'Approved', class: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' },
  sent: { label: 'Sent', class: 'text-blue-400 bg-blue-400/10 border-blue-500/30' },
  responded: { label: 'Responded', class: 'text-teal-400 bg-teal-400/10 border-teal-500/30' },
};

const SECTIONS = [
  { key: 'draft', title: 'Drafts', hint: 'finalize to send for approval', color: 'text-amber-400', collapsible: true },
  { key: 'finalized', title: 'Pending approval', hint: 'on the team meeting agenda', color: 'text-purple-400', collapsible: true },
  { key: 'approved', title: 'Ready to send', hint: 'mark as sent once mailed', color: 'text-emerald-400' },
  { key: 'sent', title: 'Sent — awaiting response', hint: '45-day response window', color: 'text-blue-400', collapsible: true },
  { key: 'responded', title: 'Responded', hint: 'response received', color: 'text-teal-400' },
  { key: 'deal_passed', title: 'Deal passed', hint: 'deal was passed — excluded from the outreach counts', color: 'text-muted-foreground', collapsible: true },
];

const STEP_ORDER = ['draft', 'finalized', 'approved', 'sent', 'responded'];
const STEP_LABELS = { draft: 'Draft', finalized: 'Finalized', approved: 'Approved', sent: 'Sent', responded: 'Response' };

const daysSince = (iso) => iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86400000) : null;

function WorkflowStepper({ letter }) {
  const hist = {};
  (letter.status_history || []).forEach(h => { if (!hist[h.status]) hist[h.status] = h; });
  if (!hist.draft) hist.draft = { at: letter.created_at };
  if (letter.approved_at && !hist.approved) hist.approved = { at: letter.approved_at };
  if (letter.sent_at && !hist.sent) hist.sent = { at: letter.sent_at };
  if (letter.responded_at && !hist.responded) hist.responded = { at: letter.responded_at };
  const currentIdx = STEP_ORDER.indexOf(letter.status);
  return (
    <div className="bg-card border border-border rounded-sm px-4 py-3 overflow-x-auto" data-testid="letter-workflow-stepper">
      <div className="flex items-start min-w-[540px]">
        {STEP_ORDER.map((s, i) => {
          const done = i <= currentIdx;
          const h = hist[s];
          return (
            <React.Fragment key={s}>
              {i > 0 && <div className={`flex-1 h-px mt-2.5 mx-2 ${i <= currentIdx ? 'bg-blue-500/60' : 'bg-border'}`} />}
              <div className="flex flex-col items-center gap-1 shrink-0" data-testid={`workflow-step-${s}`}>
                <div className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                  done
                    ? (i === currentIdx ? 'bg-blue-600 border-blue-500 text-white' : 'bg-blue-600/20 border-blue-500/50 text-blue-400')
                    : 'bg-secondary border-border text-muted-foreground/40'}`}>
                  {done && i < currentIdx ? <Check className="w-3 h-3" /> : <span className="text-[9px] font-mono">{i + 1}</span>}
                </div>
                <span className={`text-[10px] font-medium ${done ? 'text-foreground' : 'text-muted-foreground/50'}`}>{STEP_LABELS[s]}</span>
                <span className="text-[9px] font-mono text-muted-foreground/60 h-3 whitespace-nowrap">
                  {done && h?.at ? new Date(h.at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : ''}
                  {done && h?.by ? ` · ${h.by.split(' ')[0]}` : ''}
                </span>
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

const FOLLOWUP_OUTCOME_STYLES = {
  interested: { label: 'Phone: interested', class: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30' },
  later: { label: 'Phone: try later', class: 'text-amber-400 bg-amber-400/10 border-amber-500/30' },
  no_interest: { label: 'Phone: no interest', class: 'text-red-400 bg-red-400/10 border-red-500/30' },
};

function LetterChips({ letter }) {
  const waitDays = letter.status === 'approved' ? daysSince(letter.approved_at || letter.updated_at || letter.created_at) : null;
  const sentDays = letter.status === 'sent' ? daysSince(letter.sent_at) : null;
  return (
    <>
      {letter.deal_passed && (
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-red-500/25 bg-red-500/5 text-red-300/70 font-mono uppercase inline-flex items-center gap-1"
          data-testid={`letter-deal-passed-${letter.id}`} title="Der Deal wurde gepasst — dieser Brief zählt nicht mehr in den Outreach-Zahlen">
          Deal passed
        </span>
      )}
      {letter.stale_approved && (
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-red-500/30 bg-red-500/10 text-red-400 font-mono uppercase inline-flex items-center gap-1" data-testid={`letter-stale-${letter.id}`}>
          <AlertTriangle className="w-2.5 h-2.5" /> Not sent for {waitDays}d
        </span>
      )}
      {!letter.has_landing && ['approved', 'sent', 'responded'].includes(letter.status) && (
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-400 font-mono uppercase inline-flex items-center gap-1" data-testid={`letter-no-landing-${letter.id}`}>
          <QrCode className="w-2.5 h-2.5" /> No landing page
        </span>
      )}
      {letter.no_response && (
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono uppercase inline-flex items-center gap-1" data-testid={`letter-no-response-${letter.id}`}>
          <MailX className="w-2.5 h-2.5" /> No response · 45+ days
        </span>
      )}
      {letter.followup_task_at && !letter.followup_outcome && ['sent'].includes(letter.status) && (
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-orange-500/30 bg-orange-500/10 text-orange-400 font-mono uppercase inline-flex items-center gap-1" data-testid={`letter-followup-${letter.id}`}
          title="No scan/response after 21 days — a phone follow-up task was created automatically">
          <PhoneOutgoing className="w-2.5 h-2.5" /> Follow-up task
        </span>
      )}
      {letter.followup_outcome && FOLLOWUP_OUTCOME_STYLES[letter.followup_outcome] && (
        <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase inline-flex items-center gap-1 ${FOLLOWUP_OUTCOME_STYLES[letter.followup_outcome].class}`}
          data-testid={`letter-followup-outcome-${letter.id}`}
          title={`Phone follow-up by ${letter.followup_outcome_by || ''}${letter.followup_outcome_at ? ` on ${new Date(letter.followup_outcome_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })}` : ''}`}>
          <PhoneOutgoing className="w-2.5 h-2.5" /> {FOLLOWUP_OUTCOME_STYLES[letter.followup_outcome].label}
        </span>
      )}
      {letter.include_headline && letter.letter_type !== 'email' && (
        <span className="text-[9px] px-1.5 py-0.5 rounded border border-sky-500/30 bg-sky-500/10 text-sky-300 font-mono uppercase inline-flex items-center gap-1"
          data-testid={`letter-headline-chip-${letter.id}`} title="Die iAuto-Maschine schreibt die Überschrift über diesen Brief">
          <Heading className="w-2.5 h-2.5" /> Überschrift
        </span>
      )}
      {letter.status === 'responded' && (
        letter.response_sentiment ? (
          <span className={`text-[9px] px-1.5 py-0.5 rounded border font-mono uppercase inline-flex items-center gap-1 ${letter.response_sentiment === 'positive'
            ? 'text-emerald-400 bg-emerald-400/10 border-emerald-500/30'
            : 'text-red-400 bg-red-400/10 border-red-500/30'}`}
            data-testid={`letter-sentiment-${letter.id}`}
            title={letter.response_ai?.summary ? `AI: ${letter.response_ai.summary}` : undefined}>
            {letter.response_sentiment === 'positive' ? <ThumbsUp className="w-2.5 h-2.5" /> : <ThumbsDown className="w-2.5 h-2.5" />}
            {letter.response_sentiment === 'positive' ? 'Positive' : 'Negative'}
          </span>
        ) : (
          <span className="text-[9px] px-1.5 py-0.5 rounded border border-border bg-secondary text-muted-foreground font-mono uppercase"
            data-testid={`letter-sentiment-${letter.id}`} title="Rate this response as positive or negative on the letter">
            Unrated
          </span>
        )
      )}
      {['sent', 'responded'].includes(letter.status) && letter.has_landing && (
        <span className="text-[9px] font-mono text-muted-foreground inline-flex items-center gap-2">
          <span className={`inline-flex items-center gap-0.5 ${letter.landing_visits > 0 ? 'text-emerald-400' : ''}`}>
            <Eye className="w-2.5 h-2.5" /> {letter.landing_visits}
          </span>
          <span className={`inline-flex items-center gap-0.5 ${letter.landing_leads > 0 ? 'text-amber-400' : ''}`}>
            <PhoneIncoming className="w-2.5 h-2.5" /> {letter.landing_leads}
          </span>
        </span>
      )}
      {sentDays !== null && sentDays >= 0 && !letter.no_response && (
        <span className="text-[9px] font-mono text-muted-foreground/60">sent {sentDays}d ago</span>
      )}
    </>
  );
}

export default function OutreachPage() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [letters, setLetters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeLetter, setActiveLetter] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [assignments, setAssignments] = useState(null);
  const [sentDialog, setSentDialog] = useState(null); // { hasLanding: bool|null }
  const [respondDialog, setRespondDialog] = useState(false);
  const [respondSentiment, setRespondSentiment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [statusBusy, setStatusBusy] = useState(false);
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackNote, setSendBackNote] = useState('');
  const [sendBackBusy, setSendBackBusy] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({ draft: true, finalized: true, sent: true, deal_passed: true });
  const [spellBusy, setSpellBusy] = useState(false);
  const [spellResult, setSpellResult] = useState(null);
  const [applyBusy, setApplyBusy] = useState(false);
  const [spellAccept, setSpellAccept] = useState({});  // issue index → false when unticked (Max, 28.10.2026)

  // Configured letter drafter/approver may work on letters too (not just admins).
  const isDrafter = !!(user && assignments?.letter_drafter?.id === user.id);
  const isApprover = !!(user && assignments?.letter_approver?.id === user.id);
  const isFinalApprover = !!(user && assignments?.final_approver?.id === user.id);
  const canEditLetters = isAdmin || isDrafter || isApprover || isFinalApprover || !!(user && Object.values(assignments?.prio_roles || {}).some(r => r?.writer?.id === user.id));
  // Per-letter roles (22.10.2026): the prio writer drafts, the final approver (Max) reviews — production (Talu) prints & sends.
  const isWriterOf = (l) => !!(user && (l?.writer_id ? l.writer_id === user.id : isDrafter));
  const isApproverOf = (l) => !!(user && (l?.letter_approver_id ? l.letter_approver_id === user.id : isApprover));
  const canActOn = (l) => isAdmin || isApprover || isApproverOf(l);
  const canWrite = (l) => isAdmin || isApproverOf(l) || isWriterOf(l) || l?.letter_kind === 'breakup';
  // Once finalized, the drafter is locked out — only approver/admin may edit or act.
  const letterLocked = (l) => !isAdmin && !isApproverOf(l) && (l?.status || 'draft') !== 'draft';

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await api.listLetters(); setLetters(res.data); }
    catch { toast.error('Failed to load letters'); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.getWorkflowAssignments().then(r => setAssignments(r.data)).catch(() => {});
  }, []);

  const handleView = async (letterId) => {
    try {
      const res = await api.getLetter(letterId);
      setActiveLetter(res.data);
      setEditing(false);
    } catch { toast.error('Failed to load letter'); }
  };

  const handleSave = async () => {
    if (!activeLetter) return;
    setSaving(true);
    try {
      await api.updateLetter(activeLetter.id, { body: editBody });
      try { const r = await api.getLetter(activeLetter.id); setActiveLetter(r.data); }
      catch { setActiveLetter(prev => ({ ...prev, body: editBody, spellcheck_ok: false })); }
      setEditing(false);
      toast.success('Letter saved');
      load();
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleSpellcheck = async () => {
    if (editing) { toast.error('Save your changes first — the spell-check reads the saved text'); return; }
    setSpellBusy(true);
    try {
      const res = await api.spellcheckLetter(activeLetter.id);
      if (res.data.clean) {
        toast.success('AI spell-check passed — no issues found. You can finalize now.');
        try { const r = await api.getLetter(activeLetter.id); setActiveLetter(r.data); } catch {}
      } else {
        setSpellAccept({});
        setSpellResult(res.data);
      }
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Spell-check failed — please try again');
    }
    setSpellBusy(false);
  };

  const applyCorrections = async () => {
    const accept = (spellResult?.issues || []).map((_, i) => i).filter(i => spellAccept[i] !== false);
    if (accept.length === 0) { keepOwnText(); return; }
    setApplyBusy(true);
    try {
      const res = await api.applySpellcheck(activeLetter.id, accept);
      try { const r = await api.getLetter(activeLetter.id); setActiveLetter(r.data); } catch {}
      setSpellResult(null);
      const total = spellResult.issues.length;
      if (res.data.skipped?.length) {
        toast.warning(`${res.data.applied} of ${total} corrections applied — ${res.data.skipped.length} not found in the text anymore, please check manually: ${res.data.skipped.map(s => `„${s.original}“`).join(', ')}`, { duration: 12000 });
      } else {
        toast.success(accept.length === total ? 'All corrections applied — you can finalize now.' : `${res.data.applied} of ${total} corrections applied, ${res.data.ignored} kept as written — you can finalize now.`);
      }
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to apply the corrections');
    }
    setApplyBusy(false);
  };

  const keepOwnText = async () => {
    try { const r = await api.getLetter(activeLetter.id); setActiveLetter(r.data); } catch {}
    setSpellResult(null);
    toast.success('Spell-check done — keeping your text. You can finalize now.');
  };

  const handleStatusChange = async (letterId, status, extra = {}) => {
    setStatusBusy(true);
    try {
      await api.updateLetter(letterId, { status, ...extra });
      toast.success(`Status: ${STATUS_STYLES[status]?.label || status}`);
      if (activeLetter?.id === letterId) {
        try { const r = await api.getLetter(letterId); setActiveLetter(r.data); }
        catch { setActiveLetter(prev => ({ ...prev, status })); }
      }
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && typeof d === 'object' && d.code === 'owner_contact_missing') {
        toast.error(d.message, { duration: 6000 });
        navigate(`/contacts?new=1&deal=${d.project_id}&company=${encodeURIComponent(d.company_name || '')}&type=Owner`);
      } else if (d && typeof d === 'object' && d.code === 'owner_contact_incomplete') {
        toast.error(d.message, { duration: 6000 });
        navigate(`/contacts?edit=${d.contact_id}`);
      } else {
        toast.error(typeof d === 'string' ? d : d?.message || 'Failed to update status');
      }
    }
    setStatusBusy(false);
    setSentDialog(null);
    setRespondDialog(false);
  };

  const [headlineBusy, setHeadlineBusy] = useState(false);

  const handleHeadlineToggle = async (next) => {
    if (!activeLetter) return;
    setHeadlineBusy(true);
    try {
      await api.updateLetter(activeLetter.id, { include_headline: next });
      setActiveLetter(prev => ({ ...prev, include_headline: next }));
      setLetters(ls => ls.map(l => (l.id === activeLetter.id ? { ...l, include_headline: next } : l)));
      toast.success(next ? 'Überschrift wird mitgeschrieben' : 'Brief wird ohne Überschrift geschrieben');
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Überschrift konnte nicht umgestellt werden');
    }
    setHeadlineBusy(false);
  };

  const handleSentiment = async (letterId, sentiment) => {
    try {
      await api.updateLetter(letterId, { response_sentiment: sentiment });
      toast.success(sentiment ? `Response rated as ${sentiment}` : 'Rating removed');
      if (activeLetter?.id === letterId) setActiveLetter(prev => ({ ...prev, response_sentiment: sentiment }));
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to save rating');
    }
  };

  // Mark-sent guard: check live whether a landing page exists before confirming.
  const openSentDialog = async () => {
    setSentDialog({ hasLanding: null });
    try {
      const r = await api.getLandingByLetter(activeLetter.id);
      setSentDialog({ hasLanding: !!r.data?.page });
    } catch {
      setSentDialog({ hasLanding: false });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.deleteLetter(deleteTarget.id);
      toast.success('Letter deleted');
      if (activeLetter?.id === deleteTarget.id) setActiveLetter(null);
      setDeleteTarget(null);
      load();
    } catch { toast.error('Failed to delete'); }
    setDeleting(false);
  };

  const handleSendBack = async () => {
    setSendBackBusy(true);
    try {
      await api.sendLetterBack(activeLetter.id, { note: sendBackNote.trim() });
      toast.success(`Letter sent back to ${assignments?.letter_drafter?.name?.split(' ')[0] || 'the drafter'} — status: Draft`);
      setSendBackOpen(false);
      setSendBackNote('');
      try { const r = await api.getLetter(activeLetter.id); setActiveLetter(r.data); } catch {}
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send back'); }
    setSendBackBusy(false);
  };

  const handleTakeBack = async () => {
    if (!window.confirm('Take this letter back to Draft? The approver will be notified that no review is needed right now.')) return;
    setStatusBusy(true);
    try {
      await api.takeLetterBack(activeLetter.id);
      toast.success('Letter taken back — status: Draft');
      try { const r = await api.getLetter(activeLetter.id); setActiveLetter(r.data); } catch {}
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to take back'); }
    setStatusBusy(false);
  };

  const handleCopy = () => {
    if (!activeLetter) return;
    navigator.clipboard.writeText(activeLetter.body);
    setCopied(true); toast.success('Copied to clipboard');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await api.exportAllLettersPdf();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'OLTA_Outreach_Letters.pdf'; a.click();
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch { toast.error('Failed to export PDF'); }
    setExporting(false);
  };

  const handleExportDocx = async () => {
    setExportingDocx(true);
    try {
      const res = await api.exportAllLettersDocx();
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `OLTA_Outreach_Letters_${new Date().toISOString().slice(0, 10)}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success('ZIP with Word files downloaded');
    } catch { toast.error('Failed to export Word files'); }
    setExportingDocx(false);
  };

  const handleExportJson = async () => {
    try {
      const res = await api.exportAllLettersJson();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `OLTA_Outreach_Letters_${new Date().toISOString().slice(0, 10)}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success('JSON downloaded');
    } catch { toast.error('Failed to export JSON'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const spellIssues = spellResult?.issues || [];
  const selectedCount = spellIssues.filter((_, i) => spellAccept[i] !== false).length;

  const dialogs = (
    <>
      {/* AI Spell-check results */}
      <Dialog open={!!spellResult} onOpenChange={(o) => { if (!o) keepOwnText(); }}>
        <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[80vh] flex flex-col" data-testid="spellcheck-result-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-violet-400" /> AI Spell-Check — {spellResult?.issues?.length} issue{spellResult?.issues?.length !== 1 ? 's' : ''} found
            </DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground" data-testid="spellcheck-select-bar">
            <span data-testid="spellcheck-selected-count">{selectedCount} of {spellIssues.length} selected</span>
            <span className="flex gap-2">
              <button onClick={() => setSpellAccept({})} className="text-sky-300 hover:text-sky-200 underline underline-offset-2" data-testid="spellcheck-select-all-btn">All</button>
              <button onClick={() => setSpellAccept(Object.fromEntries(spellIssues.map((_, i) => [i, false])))} className="text-sky-300 hover:text-sky-200 underline underline-offset-2" data-testid="spellcheck-select-none-btn">None</button>
            </span>
          </div>
          <div className="flex-1 overflow-y-auto space-y-2 pr-1">
            {spellIssues.map((iss, i) => {
              const on = spellAccept[i] !== false;
              return (
                <label key={i} className={`flex items-start gap-2.5 border rounded-sm p-2.5 cursor-pointer transition-colors ${on ? 'border-border bg-secondary/30' : 'border-border/50 bg-transparent opacity-60'}`} data-testid={`spellcheck-issue-${i}`}>
                  <input type="checkbox" checked={on} onChange={(e) => setSpellAccept(prev => ({ ...prev, [i]: e.target.checked }))}
                    className="mt-0.5 accent-emerald-500" data-testid={`spellcheck-issue-check-${i}`} />
                  <span className="min-w-0">
                    <p className="text-xs">
                      <span className="text-red-400 line-through">{iss.original}</span>
                      <span className="text-muted-foreground mx-1.5">→</span>
                      <span className="text-emerald-300">{iss.corrected}</span>
                    </p>
                    {iss.note && <p className="text-[10px] text-muted-foreground mt-1">{iss.note}</p>}
                  </span>
                </label>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground">
            Only the ticked suggestions are applied to your text; unticked ones stay as written. Either way the check is done and Finalize unlocks.
            Editing the letter afterwards requires a new check.
          </p>
          <div className="flex justify-end gap-2 mt-1">
            <Button variant="ghost" onClick={keepOwnText} data-testid="spellcheck-keep-btn"
              className="text-muted-foreground rounded-sm text-xs h-8 px-3">Keep my text</Button>
            <Button onClick={applyCorrections} disabled={applyBusy} data-testid="spellcheck-apply-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white rounded-sm text-xs h-8 px-3">
              {applyBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />}
              {selectedCount === spellIssues.length ? 'Apply all corrections' : selectedCount === 0 ? 'Keep my text' : `Apply ${selectedCount} selected`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Mark sent confirmation with landing-page guard */}
      <Dialog open={!!sentDialog} onOpenChange={(o) => !o && setSentDialog(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="mark-sent-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Send className="w-5 h-5 text-blue-400" /> Mark letter as sent?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              This starts the <span className="text-foreground font-medium">45-day response window</span> for{' '}
              <span className="text-foreground font-medium">{activeLetter?.company_name}</span>. When a response arrives
              via the landing page, the letter moves to <span className="text-teal-400 font-medium">Responded</span> automatically.
            </p>
            {sentDialog?.hasLanding === null && (
              <p className="text-xs text-muted-foreground flex items-center gap-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking landing page…</p>
            )}
            {sentDialog?.hasLanding === false && (
              <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm px-3 py-2.5 flex items-start gap-2" data-testid="sent-dialog-no-landing-warning">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                <p className="text-xs text-amber-300 leading-relaxed">
                  <span className="font-medium">No landing page exists for this letter.</span> The printed QR code will not work
                  and responses cannot be tracked. Create the landing page below before sending.
                </p>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setSentDialog(null)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={() => handleStatusChange(activeLetter.id, 'sent')} disabled={statusBusy || sentDialog?.hasLanding === null}
              data-testid="mark-sent-confirm-btn"
              className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {statusBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Send className="w-3 h-3 mr-1" /> Mark as sent</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual response confirmation */}
      <Dialog open={respondDialog} onOpenChange={setRespondDialog}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="mark-responded-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Reply className="w-5 h-5 text-teal-400" /> Response received?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Mark <span className="text-foreground font-medium">{activeLetter?.company_name}</span> as responded.
            Use this when the owner replied by <span className="text-foreground">phone, email or post</span> —
            landing-page responses are tracked automatically.
          </p>
          <div className="flex items-center gap-2 flex-wrap" data-testid="respond-sentiment-picker">
            <span className="text-xs text-muted-foreground">How was it?</span>
            <button type="button" data-testid="respond-sentiment-positive"
              onClick={() => setRespondSentiment(s => (s === 'positive' ? null : 'positive'))}
              className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${respondSentiment === 'positive'
                ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300'
                : 'border-border text-muted-foreground hover:text-foreground'}`}>
              <ThumbsUp className="w-3 h-3" /> Positive
            </button>
            <button type="button" data-testid="respond-sentiment-negative"
              onClick={() => setRespondSentiment(s => (s === 'negative' ? null : 'negative'))}
              className={`text-xs px-3 py-1.5 rounded-full border inline-flex items-center gap-1.5 transition-colors ${respondSentiment === 'negative'
                ? 'border-red-500/60 bg-red-500/15 text-red-300'
                : 'border-border text-muted-foreground hover:text-foreground'}`}>
              <ThumbsDown className="w-3 h-3" /> Negative
            </button>
            <span className="text-[10px] text-muted-foreground/60">optional — you can rate it later</span>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setRespondDialog(false)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={() => handleStatusChange(activeLetter.id, 'responded', respondSentiment ? { response_sentiment: respondSentiment } : {})} disabled={statusBusy}
              data-testid="mark-responded-confirm-btn"
              className="bg-teal-600 hover:bg-teal-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {statusBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Response received'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Letter version history with diff */}
      <LetterHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} letter={activeLetter} />

      {/* Send letter back to the drafter */}
      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="send-back-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Undo2 className="w-5 h-5 text-amber-400" /> Send letter back to draft?
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The letter for <span className="text-foreground font-medium">{activeLetter?.company_name}</span> goes back
              to <span className="text-amber-300 font-medium">Draft</span> —{' '}
              <span className="text-foreground font-medium">{assignments?.letter_drafter?.name?.split(' ')[0] || 'the drafter'}</span> is
              notified and can edit it again.
            </p>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">What should be changed? (optional, included in the notification)</label>
              <textarea value={sendBackNote} onChange={e => setSendBackNote(e.target.value)} rows={3}
                placeholder="e.g. Please shorten the second paragraph and fix the recipient name…"
                data-testid="send-back-note-input"
                className="w-full bg-secondary border border-border rounded-sm p-2 text-xs text-foreground resize-y focus:outline-none focus:border-amber-500/50" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setSendBackOpen(false)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={handleSendBack} disabled={sendBackBusy} data-testid="send-back-confirm-btn"
              className="bg-amber-600 hover:bg-amber-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {sendBackBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Undo2 className="w-3 h-3 mr-1" /> Send back to draft</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete letter confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-card border-border rounded-sm max-w-sm" data-testid="letter-delete-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-lg text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-400" /> Delete letter?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The letter for <span className="text-foreground font-medium">{deleteTarget?.company_name}</span> will be deleted permanently.
            {deleteTarget?.has_landing && <span> Its landing page is kept and can be deleted separately.</span>}
          </p>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-xs h-8 px-3">Cancel</Button>
            <Button onClick={handleDelete} disabled={deleting} data-testid="letter-delete-confirm-btn"
              className="bg-red-600 hover:bg-red-500 text-white rounded-sm font-medium text-xs h-8 px-3">
              {deleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Delete permanently'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );

  // ─── Letter Detail View ─────────────────
  if (activeLetter) {
    const st = STATUS_STYLES[activeLetter.status] || STATUS_STYLES.draft;
    return (
      <div className="space-y-4 max-w-4xl" data-testid="letter-detail-view">
        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="ghost" onClick={() => setActiveLetter(null)} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1 min-w-[200px]">
            <h1 className="font-heading text-2xl text-foreground tracking-tight">{activeLetter.company_name}</h1>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <p className="text-sm text-muted-foreground">
                To: {activeLetter.recipient_name} —
                Created {new Date(activeLetter.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
              </p>
              {activeLetter.project_id && (
                <button onClick={() => navigate(`/projects/${activeLetter.project_id}`)} data-testid="letter-deal-link"
                  className="inline-flex items-center gap-1 text-[10px] font-mono border border-blue-500/30 bg-blue-500/10 text-blue-300 rounded px-1.5 py-0.5 hover:bg-blue-500/20 transition-colors">
                  <FileText className="w-2.5 h-2.5" /> Open deal
                </button>
              )}
              {activeLetter.owner_contact?.id && (
                <button onClick={() => navigate(`/contacts?edit=${activeLetter.owner_contact.id}`)} data-testid="letter-owner-link"
                  className="inline-flex items-center gap-1 text-[10px] font-mono border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 rounded px-1.5 py-0.5 hover:bg-emerald-500/20 transition-colors">
                  <UserRound className="w-2.5 h-2.5" /> Owner: {activeLetter.owner_contact.name}
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-mono border rounded px-2 py-1 ${st.class}`}>{st.label}</span>
            {canEditLetters && (
              <>
                {activeLetter.status === 'draft' && !letterLocked(activeLetter) && canWrite(activeLetter) && (
                  <>
                    <Button onClick={handleSpellcheck} size="sm" disabled={spellBusy || statusBusy} data-testid="spellcheck-letter-btn"
                      className={activeLetter.spellcheck_ok
                        ? 'bg-emerald-900/40 hover:bg-emerald-800/50 text-emerald-300 border border-emerald-600/40 rounded-sm text-xs h-8 px-3'
                        : 'bg-violet-700 hover:bg-violet-600 text-white rounded-sm text-xs h-8 px-3'}>
                      {spellBusy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Wand2 className="w-3.5 h-3.5 mr-1" />}
                      {spellBusy ? 'Checking…' : activeLetter.spellcheck_ok ? 'Spell-check passed' : 'AI Spell-Check'}
                    </Button>
                    <Button onClick={() => handleStatusChange(activeLetter.id, 'finalized')} size="sm"
                      disabled={statusBusy || spellBusy || !activeLetter.spellcheck_ok}
                      title={!activeLetter.spellcheck_ok ? 'Run the mandatory AI spell-check first'
                        : (activeLetter.needs_review === false || isApproverOf(activeLetter)) && activeLetter.letter_kind !== 'breakup'
                          ? 'Du bist der finale Approver — Finalisieren gibt den Brief direkt frei (kein Review)'
                          : `Finalisieren → ${activeLetter.letter_approver_name?.split(' ')[0] || 'der Approver'} prüft den Brief`}
                      className="bg-purple-700 hover:bg-purple-600 text-white rounded-sm text-xs h-8 px-3 disabled:opacity-40" data-testid="finalize-letter-btn">
                      <CheckCircle className="w-3.5 h-3.5 mr-1" />
                      {(activeLetter.needs_review === false || isApproverOf(activeLetter)) && activeLetter.letter_kind !== 'breakup' ? 'Finalize & approve' : 'Finalize'}
                    </Button>
                  </>
                )}
                {activeLetter.status === 'finalized' && (isAdmin || isApproverOf(activeLetter)) && (
                  <Button onClick={() => handleStatusChange(activeLetter.id, 'approved')} size="sm" disabled={statusBusy} data-testid="approve-letter-btn"
                    title="Brief & Landing Page geprüft — freigeben, geht in die iAuto-Produktion"
                    className="bg-emerald-700 hover:bg-emerald-600 text-white rounded-sm text-xs h-8 px-3">
                    <CheckCircle className="w-3.5 h-3.5 mr-1" /> Approve
                  </Button>
                )}
                {['finalized', 'approved'].includes(activeLetter.status) && (isAdmin || isApproverOf(activeLetter)) && (
                  <Button onClick={() => setSendBackOpen(true)} size="sm" disabled={statusBusy} data-testid="send-back-btn"
                    className="bg-amber-700/80 hover:bg-amber-600 text-white rounded-sm text-xs h-8 px-3">
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Send back to draft
                  </Button>
                )}
                {activeLetter.status === 'finalized' && isWriterOf(activeLetter) && !isAdmin && !isApproverOf(activeLetter) && (
                  <Button onClick={handleTakeBack} size="sm" disabled={statusBusy} data-testid="take-back-btn"
                    className="bg-amber-700/80 hover:bg-amber-600 text-white rounded-sm text-xs h-8 px-3">
                    <Undo2 className="w-3.5 h-3.5 mr-1" /> Take back to draft
                  </Button>
                )}
                {activeLetter.status === 'approved' && canActOn(activeLetter) && (
                  <Button onClick={openSentDialog} size="sm" data-testid="mark-sent-btn"
                    className="bg-blue-700 hover:bg-blue-600 text-white rounded-sm text-xs h-8 px-3">
                    <Send className="w-3.5 h-3.5 mr-1" /> Mark Sent
                  </Button>
                )}
                {activeLetter.status === 'sent' && canActOn(activeLetter) && (
                  <Button onClick={() => { setRespondSentiment(null); setRespondDialog(true); }} size="sm" data-testid="mark-responded-btn"
                    className="bg-teal-700 hover:bg-teal-600 text-white rounded-sm text-xs h-8 px-3">
                    <Reply className="w-3.5 h-3.5 mr-1" /> Response received
                  </Button>
                )}
                {activeLetter.status === 'responded' && canActOn(activeLetter) && (
                  <div className="inline-flex items-center gap-1" data-testid="letter-sentiment-toggle">
                    <Button onClick={() => handleSentiment(activeLetter.id, activeLetter.response_sentiment === 'positive' ? null : 'positive')}
                      size="sm" variant="outline" data-testid="sentiment-positive-btn"
                      className={`rounded-full text-xs h-8 px-3 ${activeLetter.response_sentiment === 'positive'
                        ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 hover:text-emerald-200'
                        : 'border-border text-muted-foreground hover:text-foreground'}`}>
                      <ThumbsUp className="w-3.5 h-3.5 mr-1" /> Positive
                    </Button>
                    <Button onClick={() => handleSentiment(activeLetter.id, activeLetter.response_sentiment === 'negative' ? null : 'negative')}
                      size="sm" variant="outline" data-testid="sentiment-negative-btn"
                      className={`rounded-full text-xs h-8 px-3 ${activeLetter.response_sentiment === 'negative'
                        ? 'border-red-500/60 bg-red-500/15 text-red-300 hover:bg-red-500/25 hover:text-red-200'
                        : 'border-border text-muted-foreground hover:text-foreground'}`}>
                      <ThumbsDown className="w-3.5 h-3.5 mr-1" /> Negative
                    </Button>
                    {activeLetter.response_ai?.summary && (
                      <span className="text-[10px] text-muted-foreground italic max-w-[280px] truncate" title={activeLetter.response_ai.summary}
                        data-testid="letter-ai-summary">
                        AI: {activeLetter.response_ai.summary}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}
            <a href={`${API_BASE}/api/outreach/letters/${activeLetter.id}/pdf`} target="_blank" rel="noopener noreferrer">
              <Button size="sm" className="bg-blue-900/30 hover:bg-blue-900/50 text-blue-400 border border-blue-900/30 rounded-sm text-xs h-8 px-3">
                <Download className="w-3.5 h-3.5 mr-1" /> PDF
              </Button>
            </a>
            <Button onClick={handleCopy} size="sm" className="bg-secondary hover:bg-secondary/80 text-foreground rounded-sm text-xs h-8 px-3">
              {copied ? <Check className="w-3.5 h-3.5 mr-1" /> : <Copy className="w-3.5 h-3.5 mr-1" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
            {(activeLetter.body_versions?.length || 0) > 1 && (
              <Button onClick={() => setHistoryOpen(true)} variant="outline" size="sm" data-testid="letter-history-btn"
                className="border-border text-muted-foreground hover:text-foreground rounded-sm text-xs h-8 px-3">
                <History className="w-3.5 h-3.5 mr-1" /> History · v{activeLetter.body_versions[activeLetter.body_versions.length - 1].v}
              </Button>
            )}
          </div>
        </div>

        <WorkflowStepper letter={activeLetter} />

        {activeLetter.prio && activeLetter.writer_name && (
          <div className="flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground" data-testid="letter-roles-row">
            <span className={`text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 ${PRIO_STYLES[activeLetter.prio] || ''}`} data-testid="letter-prio-badge">Prio {activeLetter.prio}</span>
            <span><PenTool className="w-3 h-3 inline mr-1" />Schreibt: <span className="text-foreground">{activeLetter.writer_name}</span></span>
            <span>·</span>
            {activeLetter.needs_review === false && activeLetter.letter_kind !== 'breakup'
              ? <span className="text-emerald-300" data-testid="letter-no-review-hint">Kein Review — {activeLetter.writer_name.split(' ')[0]} ist zugleich finaler Approver (Finalisieren = Freigabe)</span>
              : <span><CheckCircle className="w-3 h-3 inline mr-1" />Freigabe: <span className="text-foreground">{activeLetter.letter_approver_name}</span></span>}
            {!canWrite(activeLetter) && !isAdmin && (
              <span className="text-amber-300" data-testid="letter-other-writer-hint">· Dieser Brief gehört zu {activeLetter.writer_name.split(' ')[0]} — nur lesen</span>
            )}
          </div>
        )}

        {activeLetter.letter_type !== 'email' && (
          <div className="bg-card border border-border rounded-sm px-4 py-3 flex items-center gap-3" data-testid="letter-headline-row">
            <Heading className="w-4 h-4 text-sky-400 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-foreground">Überschrift über dem Brief</p>
              <p className="text-[11px] text-muted-foreground">
                {activeLetter.include_headline
                  ? 'Die iAuto-Maschine schreibt die Überschrift aus den iAuto-Einstellungen (z. B. „Haben Sie schon einmal über Ihre Nachfolge nachgedacht?") über den Brief.'
                  : 'Keine Überschrift — der Brief beginnt direkt mit der Anrede.'}
              </p>
            </div>
            <Switch checked={!!activeLetter.include_headline}
              disabled={!canEditLetters || letterLocked(activeLetter) || !canWrite(activeLetter) || headlineBusy}
              onCheckedChange={handleHeadlineToggle}
              data-testid="letter-headline-toggle" />
          </div>
        )}

        {activeLetter.status === 'draft' && activeLetter.sendback && (
          <div className="border border-amber-500/30 bg-amber-500/10 rounded-sm px-3 py-2.5 flex items-start gap-2" data-testid="sendback-banner">
            <Undo2 className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-300 leading-relaxed">
              <span className="font-medium">Sent back by {activeLetter.sendback.by}</span>
              {' '}({new Date(activeLetter.sendback.at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })})
              {activeLetter.sendback.note ? <> — „{activeLetter.sendback.note}"</> : ' — please revise and finalize again.'}
            </p>
          </div>
        )}

        <div className="bg-white border border-border rounded-sm p-6">
          {editing ? (
            <div className="space-y-3">
              <textarea value={editBody} onChange={e => setEditBody(e.target.value)}
                className="w-full min-h-[400px] bg-white border border-gray-200 rounded-sm p-4 text-sm text-gray-800 font-mono leading-relaxed resize-y focus:outline-none focus:border-blue-500"
                data-testid="letter-edit-textarea" />
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setEditing(false)} className="text-xs h-8">Cancel</Button>
                <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-xs h-8 px-4">
                  {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null} Save
                </Button>
              </div>
            </div>
          ) : (
            <div>
              <pre className="whitespace-pre-wrap text-sm text-gray-800 leading-relaxed" data-testid="letter-body">
                {activeLetter.body}
              </pre>
              {canEditLetters && !letterLocked(activeLetter) && canWrite(activeLetter) && (
                <Button onClick={() => { setEditBody(activeLetter.body); setEditing(true); }}
                  variant="ghost" className="mt-4 text-xs text-muted-foreground hover:text-foreground" data-testid="edit-letter-btn">
                  <PenTool className="w-3.5 h-3.5 mr-1" /> Edit Letter
                </Button>
              )}
              {canEditLetters && letterLocked(activeLetter) && (
                <p className="mt-4 text-[11px] text-muted-foreground flex items-center gap-1.5" data-testid="letter-locked-hint">
                  <Lock className="w-3 h-3" /> Finalized — ask {(activeLetter.letter_approver_name || assignments?.final_approver?.name || assignments?.letter_approver?.name || 'the approver').split(' ')[0]} to
                  send it back to Draft if changes are needed.
                </p>
              )}
            </div>
          )}
        </div>

        <LandingPageCard letter={activeLetter} canEdit={canEditLetters && canWrite(activeLetter)} />
        {dialogs}
      </div>
    );
  }

  // ─── Letter List View (grouped by workflow stage) ──────────────────
  const activeLetters = letters.filter(l => !l.deal_passed);
  const passedCount = letters.length - activeLetters.length;
  const byStatus = (key) => (key === 'deal_passed'
    ? letters.filter(l => l.deal_passed)
    : activeLetters.filter(l => (l.status || 'draft') === key));
  const attention = letters.filter(l => l.stale_approved).length;

  return (
    <div className="space-y-6 max-w-5xl" data-testid="outreach-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl text-foreground tracking-tight">Outreach</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {activeLetters.length} letters — {byStatus('draft').length} draft, {byStatus('finalized').length} pending approval,{' '}
            {byStatus('approved').length} ready to send, {byStatus('sent').length} sent, {byStatus('responded').length} responded
            {passedCount > 0 && <span className="text-muted-foreground/60"> · {passedCount} on passed deals (not counted)</span>}
          </p>
          {attention > 0 && (
            <p className="text-xs text-red-400 mt-1 inline-flex items-center gap-1.5" data-testid="outreach-attention-hint">
              <AlertTriangle className="w-3.5 h-3.5" />
              {attention} approved letter{attention > 1 ? 's have' : ' has'} been waiting 7+ days to be sent
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <LetterTemplateDialog canEdit={canEditLetters} />
          {isAdmin && (
            <>
              <LandingSettingsDialog />
              {letters.length > 0 && (
                <>
                  <Button onClick={handleExportJson} variant="ghost" data-testid="export-all-json"
                    className="border border-border text-foreground hover:bg-secondary rounded-sm text-sm h-9 px-4">
                    <FileJson className="w-4 h-4 mr-2" />
                    Export JSON
                  </Button>
                  <Button onClick={handleExportDocx} disabled={exportingDocx} variant="ghost" data-testid="export-all-docx"
                    className="border border-border text-foreground hover:bg-secondary rounded-sm text-sm h-9 px-4">
                    {exportingDocx ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileArchive className="w-4 h-4 mr-2" />}
                    Export Word (ZIP)
                  </Button>
                  <Button onClick={handleExportAll} disabled={exporting} data-testid="export-all-pdf"
                    className="bg-blue-600 hover:bg-blue-500 text-white rounded-sm text-sm h-9 px-4">
                    {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Export All as PDF
                  </Button>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {letters.length === 0 ? (
        <div className="bg-card border border-border rounded-sm p-12 text-center">
          <PenTool className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-foreground font-medium">No outreach letters yet</p>
          <p className="text-xs text-muted-foreground mt-1">Generate letters from approved deals on their detail page</p>
        </div>
      ) : (
        <>
        <SequenceTodayBar />
        <OutreachProgressWidget onOpenLetter={handleView} />
        {SECTIONS.map(sec => {
          const items = byStatus(sec.key);
          if (items.length === 0) return null;
          const isCollapsed = sec.collapsible && collapsed[sec.key];
          return (
            <div key={sec.key} className="space-y-2" data-testid={`letter-section-${sec.key}`}>
              {sec.collapsible ? (
                <button onClick={() => setCollapsed(c => ({ ...c, [sec.key]: !c[sec.key] }))}
                  data-testid={`letter-section-toggle-${sec.key}`}
                  className={`w-full flex items-center gap-2 text-[10px] uppercase tracking-wider font-mono ${sec.color} bg-card border border-border rounded-sm px-3 py-2 hover:bg-secondary/20 transition-colors`}>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                  {sec.title} · {items.length}
                  <span className="text-muted-foreground/50 normal-case tracking-normal">— {sec.hint}</span>
                </button>
              ) : (
                <p className={`text-[10px] uppercase tracking-wider font-mono ${sec.color}`}>
                  {sec.title} · {items.length}
                  <span className="text-muted-foreground/50 normal-case tracking-normal ml-2">— {sec.hint}</span>
                </p>
              )}
              {!isCollapsed && items.map(letter => {
                const st = STATUS_STYLES[letter.status] || STATUS_STYLES.draft;
                return (
                  <div key={letter.id}
                    className="bg-card border border-border rounded-sm p-4 flex items-center gap-4 group hover:bg-secondary/10 transition-colors"
                    data-testid={`letter-row-${letter.id}`}>
                    <div className="w-10 h-10 rounded-sm bg-purple-600/10 border border-purple-600/20 flex items-center justify-center shrink-0">
                      <PenTool className="w-5 h-5 text-purple-400" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-medium text-foreground truncate">{letter.company_name}</p>
                        <span className={`text-[9px] font-mono border rounded px-1.5 py-0.5 ${st.class}`}>{st.label}</span>
                        {/* Prio + writer in the list (Max, 27.10.2026): who has to write which letter */}
                        {letter.letter_kind !== 'breakup' && (letter.prio
                          ? <span className={`text-[9px] font-mono font-bold border rounded px-1.5 py-0.5 ${PRIO_STYLES[letter.prio] || ''}`} data-testid={`letter-row-prio-${letter.id}`}>Prio {letter.prio}</span>
                          : <span className="text-[9px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground/60" title="Deal hat noch keine Prio — Prio wird beim Freigabe-Vorschlag vergeben" data-testid={`letter-row-prio-${letter.id}`}>ohne Prio</span>)}
                        {letter.letter_kind !== 'breakup' && letter.writer_name && (letter.status || 'draft') === 'draft' && (
                          <span className="text-[10px] text-muted-foreground inline-flex items-center gap-1" title={`Schreibt: ${letter.writer_name}`} data-testid={`letter-row-writer-${letter.id}`}>
                            <PenTool className="w-3 h-3" />{letter.writer_name.split(' ')[0]}
                          </span>
                        )}
                        <LetterChips letter={letter} />
                        <LetterDupeBadge dup={letter.dup} letterId={letter.id} onRemoved={load} />
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-muted-foreground">To: {letter.recipient_name}</span>
                        <span className="text-[10px] text-muted-foreground font-mono">
                          {new Date(letter.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button variant="ghost" size="sm" onClick={() => handleView(letter.id)} data-testid={`letter-view-btn-${letter.id}`}
                        className="h-7 text-[11px] text-muted-foreground hover:text-foreground">
                        View <ChevronRight className="w-3 h-3 ml-0.5" />
                      </Button>
                      <button onClick={() => navigate(`/projects/${letter.project_id}`)}
                        title="Open deal" className="text-muted-foreground/30 hover:text-blue-400 transition-colors p-1">
                        <FileText className="w-3.5 h-3.5" />
                      </button>
                      {(isAdmin || ((isWriterOf(letter) || isApproverOf(letter)) && (letter.status || 'draft') === 'draft')) && (
                        <button onClick={() => setDeleteTarget(letter)} data-testid={`letter-delete-btn-${letter.id}`}
                          className="text-muted-foreground/30 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 p-1">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
        </>
      )}
      {dialogs}
    </div>
  );
}
